// Productivity Tab v3 — "Team / Student productivity": every active student grouped by team,
// with in-period proposal buckets and current pitch counts. Staff-only (server-gated, same
// viewAuditTrail gate as the AE overview). Canonical design: design repo
// docs/designs/2026-07-21-migration-daily-aaron-ted-angel/productivity-tab-v3.dc.html.
// Participation units and Team/Editorial/Workshop attendance have no data source yet — those
// columns render explicit "—" placeholders, not fake numbers.
// WO-2026-09-14 Track H: each student's name links to their My Progress page (the existing
// ?student= drill-down), mirroring the Team/AE overview's "Open →".
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { StaffStudentRow, StudentsDashboard } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { Badge, Card, EmptyState, Input, Spinner } from "../../components/ui.js";

const TEAM_FLAGS: Record<string, string> = {
  "United States": "🇺🇸", Berkeley: "🇺🇸", India: "🇮🇳", Nigeria: "🇳🇬", Brazil: "🇧🇷",
  Philippines: "🇵🇭", Bangladesh: "🇧🇩", Mexico: "🇲🇽", Peru: "🇵🇪", "South Africa": "🇿🇦",
  "Cape Town": "🇿🇦", Uganda: "🇺🇬", Vietnam: "🇻🇳", Ethiopia: "🇪🇹",
};
const flagFor = (team: string): string => TEAM_FLAGS[team] ?? "🌍";
const NO_TEAM = "No team assigned";

const fmtDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const initialsOf = (name: string): string => {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?";
};

/** Column with no data source yet — an explicit placeholder beats an invented number. */
function NotTracked(): JSX.Element {
  return (
    <span className="text-slate-300" title="Not tracked in the webapp yet">
      —
    </span>
  );
}

export function StudentProductivity(): JSX.Element {
  const [from, setFrom] = useState(() => fmtDate(Date.now() - 28 * 864e5));
  const [to, setTo] = useState(() => fmtDate(Date.now()));
  const [q, setQ] = useState("");
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const weeks = Math.max(0.5, Math.round(((Date.parse(to) - Date.parse(from)) / (7 * 864e5)) * 10) / 10);

  const { data, isLoading } = useQuery({
    queryKey: qk.productivityStudents(from, to),
    queryFn: () => api.get<StudentsDashboard>(`/dashboard/students?from=${from}&to=${to}`),
  });

  const buckets = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (s: StaffStudentRow, team: string): boolean =>
      !needle ||
      `${s.name ?? ""} ${s.email} ${s.domains.join(" ")} ${team}`.toLowerCase().includes(needle);
    const byTeam = new Map<string, StaffStudentRow[]>();
    for (const s of data?.students ?? []) {
      const team = s.team?.trim() || NO_TEAM;
      if (!match(s, team)) continue;
      byTeam.set(team, [...(byTeam.get(team) ?? []), s]);
    }
    return [...byTeam.entries()]
      .map(([team, students]) => ({
        team,
        students,
        proposed: students.reduce((n, s) => n + s.proposed, 0),
        unchecked: students.reduce((n, s) => n + s.unchecked, 0),
        greenlit: students.reduce((n, s) => n + s.greenlit, 0),
        rejected: students.reduce((n, s) => n + s.rejected, 0),
      }))
      .sort((a, b) => b.greenlit - a.greenlit || a.team.localeCompare(b.team));
  }, [data, q]);

  if (isLoading || !data) return <Spinner label="Loading student productivity…" />;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Team / Student productivity</h1>
          <p className="mt-0.5 text-sm text-slate-500">Reviewer proposals and greenlits by student — grouped by team.</p>
        </div>
        {/* No flex-wrap here: the ui Input is w-full, and a wrapping row lets each input take a
            whole line — the nowrap row shrinks them like the AE overview's period control. */}
        <div className="flex items-center gap-2">
          <Input
            type="search"
            className="w-60 py-1"
            placeholder="Search students, emails, domains…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <span className="text-xs font-medium text-slate-600">Period</span>
          <Input type="date" className="w-36 py-1" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="text-slate-400">–</span>
          <Input type="date" className="w-36 py-1" value={to} onChange={(e) => setTo(e.target.value)} />
          <Badge tone="slate">{weeks} weeks</Badge>
        </div>
      </header>

      {buckets.length === 0 &&
        (q.trim() ? (
          <p className="text-sm text-slate-500">No students match &ldquo;{q.trim()}&rdquo;.</p>
        ) : (
          <EmptyState>No active students in the roster yet.</EmptyState>
        ))}

      {buckets.map((b) => {
        const open = !closed.has(b.team);
        return (
          <Card key={b.team} className="overflow-hidden">
            <button
              type="button"
              className="flex w-full flex-wrap items-center gap-2.5 border-b border-slate-100 px-4 py-2.5 text-left hover:bg-slate-50"
              aria-expanded={open}
              onClick={() =>
                setClosed((cur) => {
                  const next = new Set(cur);
                  if (next.has(b.team)) next.delete(b.team);
                  else next.add(b.team);
                  return next;
                })
              }
            >
              <span className="text-lg">{flagFor(b.team)}</span>
              <span className="min-w-[8rem]">
                <span className="block text-sm font-semibold">{b.team}</span>
                <span className="block text-xs text-slate-500">
                  {b.students.length} student{b.students.length === 1 ? "" : "s"}
                </span>
              </span>
              <span className="flex flex-wrap gap-1.5">
                <Badge tone="blue">Proposed {b.proposed}</Badge>
                <Badge tone="slate">Unchecked {b.unchecked}</Badge>
                <Badge tone="green">Greenlit {b.greenlit}</Badge>
                <Badge tone="red">Reject {b.rejected}</Badge>
              </span>
              <span className="ml-auto flex items-center gap-3">
                <span className="text-right">
                  <span className="block text-lg font-bold leading-tight text-green-700">{b.greenlit}</span>
                  <span className="block text-[10px] uppercase tracking-wide text-slate-500">Greenlits</span>
                </span>
                <span className="text-xs text-slate-400">{open ? "▲" : "▼"}</span>
              </span>
            </button>
            {open && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[70rem] border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="border-b border-slate-100 px-4 py-2">Student</th>
                      <th className="border-b border-slate-100 px-2 py-2">Domain</th>
                      <th className="border-b border-slate-100 px-2 py-2">Participation (Units)</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Proposed</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Unchecked</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Greenlit</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Reject</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">To Pitch (Team)</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">To Pitch (Editorial)</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Team Mtg</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Editorial Mtg</th>
                      <th className="border-b border-slate-100 px-4 py-2 text-right">Workshop / Class</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.students.map((s) => (
                      <tr key={s.email} className="hover:bg-slate-50">
                        <td className="border-b border-slate-100 px-4 py-2">
                          <span className="flex items-center gap-2">
                            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-[11px] font-bold text-indigo-700">
                              {initialsOf(s.name ?? s.email)}
                            </span>
                            <span className="min-w-0">
                              <Link
                                to={`/my-productivity?student=${encodeURIComponent(s.email)}&back=students`}
                                className="block truncate text-sm font-medium text-brand hover:underline"
                                title="Open their productivity page"
                              >
                                {s.name ?? s.email}
                              </Link>
                              <span className="block truncate text-xs text-slate-400">{s.email}</span>
                            </span>
                          </span>
                        </td>
                        <td className="whitespace-nowrap border-b border-slate-100 px-2 py-2 text-xs text-slate-500">
                          {s.domains.join(" · ") || "—"}
                        </td>
                        <td className="whitespace-nowrap border-b border-slate-100 px-2 py-2">
                          <NotTracked />
                        </td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center">{s.proposed}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center text-slate-500">{s.unchecked}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center font-semibold text-green-700">{s.greenlit}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center text-red-800">{s.rejected}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center text-blue-800">{s.toPitchTeam}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center text-amber-800">{s.toPitchEditorial}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center"><NotTracked /></td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center"><NotTracked /></td>
                        <td className="border-b border-slate-100 px-4 py-2 text-right"><NotTracked /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}
      <p className="text-xs text-slate-400">
        Proposal buckets count complete proposals whose proposal date falls in the period, by current status
        (Student Reject / COI Detected never count as proposed). Participation units and meeting/workshop
        attendance aren&rsquo;t tracked in the webapp yet.
      </p>
    </div>
  );
}
